import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';

// PrimeNG
import { DataViewModule } from 'primeng/dataview';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';

// Services
import {
  NotificationService,
  Notification,
  NotificationType,
  NotificationPriority
} from '../../core/services/notification.service';

interface FilterOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-notifications-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DataViewModule,
    SelectModule,
    ButtonModule,
    CardModule,
    TagModule,
    TooltipModule,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  templateUrl: './notifications-page.component.html',
  styleUrls: ['./notifications-page.component.scss']
})
export class NotificationsPageComponent implements OnInit {
  notifications: Notification[] = [];
  loading = false;
  totalRecords = 0;
  
  selectedTypeFilter: string = 'all';
  selectedReadFilter: string = 'all';
  selectedPriorityFilter: string = 'all';

  typeFilters: FilterOption[] = [
    { label: 'All Types', value: 'all' },
    { label: 'Contracts', value: 'CONTRACT' },
    { label: 'Reservations', value: 'RESERVATION' },
    { label: 'Interactions', value: 'INTERACTION' },
    { label: 'Customers', value: 'CUSTOMER' },
    { label: 'Users', value: 'USER' },
    { label: 'Territories', value: 'TERRITORY' },
    { label: 'Products', value: 'PRODUCT' },
    { label: 'Subscriptions', value: 'SUBSCRIPTION' },
    { label: 'System', value: 'SYSTEM' }
  ];

  readFilters: FilterOption[] = [
    { label: 'All', value: 'all' },
    { label: 'Unread Only', value: 'unread' },
    { label: 'Read Only', value: 'read' }
  ];

  priorityFilters: FilterOption[] = [
    { label: 'All Priorities', value: 'all' },
    { label: 'Urgent', value: NotificationPriority.URGENT },
    { label: 'High', value: NotificationPriority.HIGH },
    { label: 'Medium', value: NotificationPriority.MEDIUM },
    { label: 'Low', value: NotificationPriority.LOW }
  ];

  constructor(
    private notificationService: NotificationService,
    private router: Router,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadNotifications();
  }

  loadNotifications(): void {
    this.loading = true;
    
    const filters: any = { limit: 50 };
    
    if (this.selectedReadFilter === 'unread') {
      filters.isRead = false;
    } else if (this.selectedReadFilter === 'read') {
      filters.isRead = true;
    }
    
    if (this.selectedPriorityFilter !== 'all') {
      filters.priority = this.selectedPriorityFilter;
    }

    // Type filter based on prefix
    if (this.selectedTypeFilter !== 'all') {
      // This is a simplified approach - in production, you'd filter on the backend
      this.notificationService.getNotifications(filters).subscribe({
        next: (response) => {
          if (response.success) {
            let notifications = response.data;
            
            // Client-side type filtering
            if (this.selectedTypeFilter !== 'all') {
              notifications = notifications.filter(n => 
                n.type.toString().startsWith(this.selectedTypeFilter)
              );
            }
            
            this.notifications = notifications;
            this.totalRecords = notifications.length;
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading notifications:', error);
          this.loading = false;
        }
      });
    } else {
      this.notificationService.getNotifications(filters).subscribe({
        next: (response) => {
          if (response.success) {
            this.notifications = response.data;
            this.totalRecords = response.data.length;
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading notifications:', error);
          this.loading = false;
        }
      });
    }
  }

  onFilterChange(): void {
    this.loadNotifications();
  }

  clearFilters(): void {
    this.selectedTypeFilter = 'all';
    this.selectedReadFilter = 'all';
    this.selectedPriorityFilter = 'all';
    this.loadNotifications();
  }

  handleNotificationClick(notification: Notification): void {
    // Mark as read and viewed
    if (!notification.isRead) {
      this.notificationService.markAsRead(notification._id).subscribe();
    }
    if (!notification.isViewed) {
      this.notificationService.markAsViewed(notification._id).subscribe();
    }

    // Navigate if action URL exists
    if (notification.metadata?.actionUrl) {
      const url = notification.metadata.actionUrl;
      const [path, queryString] = url.split('?');
      const queryParams: any = {};
      
      if (queryString) {
        queryString.split('&').forEach(param => {
          const [key, value] = param.split('=');
          if (key && value) {
            queryParams[decodeURIComponent(key)] = decodeURIComponent(value);
          }
        });
      }
      
      this.router.navigate([path], { queryParams });
    }
  }

  markAsRead(notification: Notification, event: Event): void {
    event.stopPropagation();
    this.notificationService.markAsRead(notification._id).subscribe({
      next: () => {
        notification.isRead = true;
      },
      error: (error) => {
        console.error('Error marking as read:', error);
      }
    });
  }

  deleteNotification(notification: Notification, event: Event): void {
    event.stopPropagation();
    
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this notification?',
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.notificationService.deleteNotification(notification._id).subscribe({
          next: () => {
            this.notifications = this.notifications.filter(n => n._id !== notification._id);
            this.totalRecords--;
          },
          error: (error) => {
            console.error('Error deleting notification:', error);
          }
        });
      }
    });
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead().subscribe({
      next: () => {
        this.notifications = this.notifications.map(n => ({
          ...n,
          isRead: true
        }));
      },
      error: (error) => {
        console.error('Error marking all as read:', error);
      }
    });
  }

  getIconClass(notification: Notification): string {
    return this.notificationService.getIconClass(notification.type);
  }

  getPrioritySeverity(priority: NotificationPriority): string {
    switch (priority) {
      case NotificationPriority.URGENT:
        return 'danger';
      case NotificationPriority.HIGH:
        return 'warning';
      case NotificationPriority.MEDIUM:
        return 'info';
      case NotificationPriority.LOW:
        return 'success';
      default:
        return 'info';
    }
  }

  getTimeAgo(date: Date): string {
    const now = new Date().getTime();
    const notifDate = new Date(date).getTime();
    const diff = now - notifDate;

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  allNotificationsRead(): boolean {
    return this.notifications.length === 0 || this.notifications.every(n => n.isRead);
  }
}

